import React, { useMemo, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import LevelSelect from '../components/LevelSelect.tsx';
import { useLogs } from '../hooks/useLogs.ts';
import { useLiveLogs } from '../hooks/useLiveLogs.ts';
import { useOverview } from '../hooks/useOverview.ts';
import GridEmpty from '../components/GridEmpty.tsx';
import { formatLogTimestamp, matchesLogFilters } from '../services/logs.ts';

const LogsPage: React.FC = () => {
  const [level, setLevel] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [from, setFrom] = React.useState<Dayjs | null>(null);
  const [to, setTo] = React.useState<Dayjs | null>(null);
  const liveEndRef = useRef<HTMLDivElement | null>(null);
  const { data: overview } = useOverview();

  const historyFilters = useMemo(
    () => ({
      level,
      query,
      from: from ? from.toISOString() : '',
      to: to ? to.toISOString() : '',
    }),
    [from, level, query, to]
  );

  const { rows, loading, loadMore, hasMore, total, error } = useLogs(historyFilters);
  const {
    rows: liveRows,
    paused,
    status,
    error: liveError,
    bufferedCount,
    togglePaused,
    clear,
  } = useLiveLogs();

  const filteredLiveRows = useMemo(
    () => liveRows.filter((row) => matchesLogFilters(row, historyFilters)),
    [historyFilters, liveRows]
  );

  React.useEffect(() => {
    if (!paused) {
      liveEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredLiveRows, paused]);

  const exportHref = `/api/v1/super/logs?level=${encodeURIComponent(level)}&query=${encodeURIComponent(query)}&from=${encodeURIComponent(from ? from.toISOString() : '')}&to=${encodeURIComponent(to ? to.toISOString() : '')}&export=csv`;

  const connectionColor = status === 'connected'
    ? 'success'
    : status === 'connecting'
      ? 'warning'
      : status === 'error'
        ? 'error'
        : 'default';

  return (
    <Box p={3}>
      <Stack spacing={3}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xs: 'stretch', xl: 'center' }}>
              <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700 }}>
                Logs workspace
              </Typography>
              <Chip label={`Live: ${status}`} color={connectionColor} variant={status === 'connected' ? 'filled' : 'outlined'} />
              {bufferedCount > 0 ? <Chip label={`Buffered: ${bufferedCount}`} variant="outlined" /> : null}
            </Stack>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <LevelSelect value={level} onChange={setLevel} />
              <TextField
                label="Search"
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <DatePicker label="From" value={from} onChange={(value) => setFrom(value)} />
              <DatePicker label="To" value={to} onChange={(value) => setTo(value)} />
              <Button variant={paused ? 'contained' : 'outlined'} onClick={togglePaused}>
                {paused ? 'Resume live stream' : 'Pause live stream'}
              </Button>
              <Button variant="outlined" color="inherit" onClick={clear}>
                Clear live buffer
              </Button>
              <Button component="a" href={exportHref} target="_blank" rel="noreferrer" variant="contained">
                Export CSV
              </Button>
            </Stack>

            {liveError ? <Alert severity="warning">{liveError}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </Paper>

        {overview ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.16em' }}>
                Connection scope
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {overview.health.status === 'degraded' ? 'Degraded' : 'Healthy'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {overview.health.logsLastHour} events observed in the last hour.
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.16em' }}>
                Error pressure
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {overview.logSummary.recentErrors} errors
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {overview.logSummary.recentWarnings} warnings in the same rolling window.
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.16em' }}>
                Platform context
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {overview.tenants.active} active tenants
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {overview.tenants.pendingInvites} invites pending while you inspect the stream.
              </Typography>
            </Paper>
          </Box>
        ) : null}

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper elevation={0} sx={{ flex: '0 0 420px', minHeight: 520, borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, bgcolor: 'grey.900', color: 'common.white' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Live stream</Typography>
              <Typography variant="body2" sx={{ color: 'grey.400' }}>
                Real-time observability from the canonical superadmin surface.
              </Typography>
            </Box>
            <Box sx={{ bgcolor: '#050816', color: '#e2e8f0', height: 440, overflowY: 'auto', px: 2, py: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
              {filteredLiveRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Waiting for live events that match the current filters.
                </Typography>
              ) : (
                filteredLiveRows.map((row) => (
                  <Box key={row.id} sx={{ pb: 1.2, borderBottom: '1px solid rgba(148,163,184,0.08)', mb: 1.2 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                      <Typography component="span" sx={{ color: '#94a3b8', fontSize: 12 }}>
                        {formatLogTimestamp(row.timestamp)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: row.level === 'ERROR'
                            ? '#fca5a5'
                            : row.level === 'WARN'
                              ? '#fde68a'
                              : row.level === 'DEBUG'
                                ? '#93c5fd'
                                : '#86efac',
                        }}
                      >
                        {row.level}
                      </Typography>
                      {row.component ? <Typography component="span" sx={{ color: '#cbd5e1', fontSize: 12 }}>{row.component}</Typography> : null}
                      {row.source ? <Typography component="span" sx={{ color: '#64748b', fontSize: 12 }}>{row.source}</Typography> : null}
                    </Stack>
                    <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5 }}>
                      {row.message}
                    </Typography>
                  </Box>
                ))
              )}
              <div ref={liveEndRef} />
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Historical logs</Typography>
              <Typography variant="body2" color="text.secondary">
                Search, paginate and export the persistent audit trail.
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {loading && rows.length === 0 ? <CircularProgress /> : (
                <>
                  <div style={{ height: 520, width: '100%' }}>
                    <DataGrid
                      rows={rows}
                      columns={[
                        {
                          field: 'timestamp',
                          headerName: 'Time',
                          width: 190,
                          valueFormatter: ({ value }) => formatLogTimestamp(String(value || '')),
                        },
                        { field: 'level', headerName: 'Level', width: 100 },
                        { field: 'source', headerName: 'Source', width: 120 },
                        { field: 'component', headerName: 'Component', flex: 1, minWidth: 160 },
                        { field: 'message', headerName: 'Message', flex: 2, minWidth: 280 },
                      ]}
                      disableRowSelectionOnClick
                      loading={loading}
                      slots={{ noRowsOverlay: GridEmpty }}
                    />
                  </div>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {total ? `${rows.length} / ${total} log entries loaded` : `${rows.length} log entries loaded`}
                    </Typography>
                    {hasMore ? (
                      <Button variant="outlined" onClick={loadMore} disabled={loading}>
                        Load more
                      </Button>
                    ) : null}
                  </Stack>
                </>
              )}
            </Box>
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

export default LogsPage;
